import type { ReactNode } from "react";
import { LangLink as Link } from "@/components/LangLink";
import { Reveal } from "@/components/Reveal";

type CtaSectionProps = {
  image?: string;
  video?: string;
  title: ReactNode;
  subtitle: ReactNode;
  buttonText: string;
  href: string;
  secondaryButtonText?: string;
  secondaryButtonHref?: string;
  secondaryButtonHiddenText?: string;
};

export function CtaSection({
  image,
  video,
  title,
  subtitle,
  buttonText,
  href,
  secondaryButtonText,
  secondaryButtonHref,
  secondaryButtonHiddenText,
}: CtaSectionProps) {
  return (
    <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden border-t border-border group/cta">
      {video ? (
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover scale-105 group-hover/cta:scale-100 transition-transform duration-[2000ms]"
          poster={image}
        >
          <source src={video} type="video/mp4" />
        </video>
      ) : (
        image && (
          <img
            src={image}
            alt={typeof title === "string" ? title : "CTA image"}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover scale-105 group-hover/cta:scale-100 transition-transform duration-[2000ms]"
            width={1920}
            height={900}
          />
        )
      )}

      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30" />

      <div className="relative container-bizu flex flex-col items-center justify-center text-center z-10 py-24 space-y-8">
        <Reveal effect="up" className="space-y-4">
          <h2 className="display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.1] text-white uppercase tracking-[0.02em] max-w-4xl mx-auto">
            {title}
          </h2>

          <p className="text-lg md:text-xl text-white/80 leading-relaxed max-w-2xl mx-auto">
            {subtitle}
          </p>
        </Reveal>

        <Reveal effect="up" delay={150} className="space-y-6">
          <div className="flex flex-wrap items-center justify-center gap-4">
            {href.startsWith("http") ? (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 rounded-full bg-accent hover:bg-accent/80 transition-colors text-base font-bold uppercase tracking-wider text-white shadow-lg inline-block"
              >
                {buttonText}
              </a>
            ) : (
              <Link
                to={href as any}
                className="px-8 py-4 rounded-full bg-accent hover:bg-accent/80 transition-colors text-base font-bold uppercase tracking-wider text-white shadow-lg inline-block"
              >
                {buttonText}
              </Link>
            )}

            {secondaryButtonText &&
              secondaryButtonHref &&
              (secondaryButtonHref.startsWith("http") ? (
                <a
                  href={secondaryButtonHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 rounded-full bg-white text-black hover:bg-neutral-200 transition-colors text-base font-bold uppercase tracking-wider shadow-lg inline-block"
                >
                  {secondaryButtonText}
                  {secondaryButtonHiddenText && <span className="sr-only"> {secondaryButtonHiddenText}</span>}
                </a>
              ) : (
                <Link
                  to={secondaryButtonHref as any}
                  className="px-8 py-4 rounded-full bg-white text-black hover:bg-neutral-200 transition-colors text-base font-bold uppercase tracking-wider shadow-lg inline-block"
                >
                  {secondaryButtonText}
                  {secondaryButtonHiddenText && <span className="sr-only"> {secondaryButtonHiddenText}</span>}
                </Link>
              ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
